/**
 * Запись осмотра с веба (founder 16.08.2026 — «делаем а сначала»): консультант
 * приехал к клиенту, добавил поле на вебе, посмотрел NDVI, записал осмотр — всё
 * с одного устройства (раньше поля заводились с веба, а осмотры только с
 * мобилки, сценарий разваливался пополам).
 *
 * Форма — 1:1 по смыслу с мобильной (`InspectionFormScreen.kt`, wireframe №9):
 * фенофаза (необязательно), находки (тип + название + степень), фото, заметка.
 * Валидация минимума та же (FR-4.6): хотя бы одна находка ИЛИ непустая заметка.
 * Осмотр append-only (ADR-001) — после сохранения формы для правки/удаления
 * нет, это сознательно (event-sourced история наблюдений).
 *
 * Фото: presign + прямой PUT в S3 (см. `lib/inspectionPhoto.ts`), сжатие на
 * клиенте (canvas), загрузка сразу по добавлению — статус виден построчно
 * (idle/uploading/done/error), не «нажал Сохранить и гадай», это важно на
 * плохой связи в поле.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Image as ImageIcon, Loader2, Plus, RotateCw, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  compressInspectionPhoto,
  uploadInspectionPhoto,
  type InspectionPhotoRef,
} from '@/lib/inspectionPhoto';
import {
  isDraftValid,
  PROBLEM_TYPE_LABEL,
  SEVERITY_OPTIONS,
  type InspectionProblem,
  type ProblemType,
  type Severity,
} from './inspectionLogic';

interface Props {
  fieldId: string;
  fieldName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * AK-938: варианты опыта на поле — осмотр можно отнести к варианту
   * (`subzone_id`) или ко всему полю. Не передан — выбора нет (как раньше).
   */
  variants?: Array<{ id: string; label: string }>;
  /** Вызывается после сохранения (карточка опыта перечитывает ленту). */
  onSaved?: () => void;
}

const WHOLE_FIELD = '__field__';

interface DraftProblem extends InspectionProblem {
  key: string;
}

interface DraftPhoto {
  id: string;
  file: File;
  previewUrl: string;
  status: 'uploading' | 'done' | 'error';
  ref?: InspectionPhotoRef;
  error?: string;
}

// Зеркалит backend INSPECTION_PHOTO_MIME (rasters/inspection_photos.py) —
// presign отклонит всё остальное (напр. image/heic с iPhone, если Safari
// почему-то не перекодировал сам).
const SUPPORTED_PHOTO_MIME = new Set(['image/jpeg', 'image/jpg', 'image/webp', 'image/png']);

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function InspectionCreateSheet({
  fieldId,
  fieldName,
  open,
  onOpenChange,
  variants,
  onSaved,
}: Props) {
  const qc = useQueryClient();

  // Client-UUID осмотра — рождается при открытии формы (идемпотентность
  // сохранения зеркалит мобильную B.4: presign фото и POST осмотра ссылаются
  // на один и тот же id, повторный сабмит после сетевого сбоя не плодит дубль).
  const [inspectionId, setInspectionId] = useState(() => newId());
  const [phase, setPhase] = useState('');
  const [subzoneId, setSubzoneId] = useState<string>(WHOLE_FIELD);
  const [note, setNote] = useState('');
  const [problems, setProblems] = useState<DraftProblem[]>([]);
  const [photos, setPhotos] = useState<DraftPhoto[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  // Мини-форма «добавить находку».
  const [findingType, setFindingType] = useState<ProblemType>('pest');
  const [findingName, setFindingName] = useState('');
  const [findingSeverity, setFindingSeverity] = useState<Severity | null>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setInspectionId(newId());
    setPhase('');
    setSubzoneId(WHOLE_FIELD);
    setNote('');
    setProblems([]);
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setSubmitError(null);
    setShowValidation(false);
    setFindingType('pest');
    setFindingName('');
    setFindingSeverity(null);
  }

  // Открыли форму заново — чистое состояние (иначе прошлый черновик утечёт).
  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/inspections', {
        id: inspectionId,
        field_id: fieldId,
        subzone_id: subzoneId === WHOLE_FIELD ? undefined : subzoneId,
        inspected_at: new Date().toISOString(),
        source_kind: 'web_manual',
        ai_extracted: {
          phase: phase.trim() || undefined,
          problems: problems.map((p) => ({
            type: p.type,
            name: p.name,
            severity: p.severity ?? undefined,
          })),
          note: note.trim() || undefined,
        },
        photos: photos
          .filter((p): p is DraftPhoto & { ref: InspectionPhotoRef } => !!p.ref)
          .map((p) => ({
            id: p.ref.id,
            url: p.ref.url,
            object_key: p.ref.object_key,
            taken_at: p.ref.taken_at,
          })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-inspections', fieldId] });
      onSaved?.();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      setSubmitError(err instanceof ApiError ? err.detail : 'Не удалось сохранить осмотр');
    },
  });

  function addFinding() {
    const name = findingName.trim();
    if (!name) return;
    setProblems((prev) => [
      ...prev,
      { key: newId(), type: findingType, name, severity: findingSeverity ?? undefined },
    ]);
    setFindingName('');
    setFindingSeverity(null);
  }

  function removeFinding(key: string) {
    setProblems((prev) => prev.filter((p) => p.key !== key));
  }

  async function uploadOnePhoto(file: File) {
    const photoId = newId();
    const previewUrl = URL.createObjectURL(file);
    setPhotos((prev) => [...prev, { id: photoId, file, previewUrl, status: 'uploading' }]);
    try {
      const compressed = await compressInspectionPhoto(file);
      const contentType = compressed.type || 'image/jpeg';
      if (!SUPPORTED_PHOTO_MIME.has(contentType)) {
        throw new Error(
          `Формат «${contentType || 'неизвестен'}» не поддерживается — используйте JPEG/PNG/WEBP`,
        );
      }
      const ref = await uploadInspectionPhoto(inspectionId, photoId, compressed, contentType);
      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, status: 'done', ref } : p)),
      );
    } catch (err) {
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId
            ? {
                ...p,
                status: 'error',
                error:
                  err instanceof ApiError
                    ? err.detail
                    : err instanceof Error
                      ? err.message
                      : 'Не удалось загрузить фото',
              }
            : p,
        ),
      );
    }
  }

  function onFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // тот же файл можно выбрать повторно
    files.forEach((f) => void uploadOnePhoto(f));
  }

  function removePhoto(id: string) {
    setPhotos((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }

  const photosUploading = photos.some((p) => p.status === 'uploading');
  const photosHaveError = photos.some((p) => p.status === 'error');
  const draftValid = isDraftValid(problems, note);

  function handleSubmit() {
    setSubmitError(null);
    if (!draftValid) {
      setShowValidation(true);
      return;
    }
    if (photosUploading || photosHaveError) return; // кнопка и так задизейблена — страховка
    createMutation.mutate();
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !createMutation.isPending && onOpenChange(o)}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Новый осмотр{fieldName ? ` · ${fieldName}` : ''}</SheetTitle>
          <SheetDescription>
            Что увидели на поле: находки, фото, заметка. Дата и автор проставятся сами.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-6">
          {/* Фенофаза */}
          <div className="space-y-2">
            {variants && variants.length > 0 && (
              <div className="space-y-2 pb-3">
                <Label>Вариант опыта</Label>
                <Select value={subzoneId} onValueChange={setSubzoneId}>
                  <SelectTrigger aria-label="Вариант опыта">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={WHOLE_FIELD}>Всё поле (все варианты)</SelectItem>
                    {variants.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Label htmlFor="insp-phase">Фенофаза · необязательно</Label>
            <Input
              id="insp-phase"
              value={phase}
              onChange={(e) => setPhase(e.target.value)}
              placeholder="Например: кущение"
            />
          </div>

          {/* Находки */}
          <div className="space-y-2">
            <Label>Находки</Label>

            {problems.length > 0 && (
              <ul className="space-y-2">
                {problems.map((p) => (
                  <li
                    key={p.key}
                    className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2"
                  >
                    <div className="min-w-0 text-sm">
                      <span className="font-medium text-foreground">{p.name}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        · {PROBLEM_TYPE_LABEL[p.type as ProblemType] ?? p.type}
                        {p.severity &&
                          ` · степень: ${SEVERITY_OPTIONS.find((s) => s.value === p.severity)?.label ?? p.severity}`}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => removeFinding(p.key)}
                      aria-label="Убрать находку"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-3">
              <Select value={findingType} onValueChange={(v) => setFindingType(v as ProblemType)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PROBLEM_TYPE_LABEL) as ProblemType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {PROBLEM_TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={findingName}
                onChange={(e) => setFindingName(e.target.value)}
                placeholder="Название"
                className="min-w-[10rem] flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFinding();
                  }
                }}
              />
              <div className="flex gap-1">
                {SEVERITY_OPTIONS.map((s) => (
                  <Button
                    key={s.value}
                    type="button"
                    size="sm"
                    variant={findingSeverity === s.value ? 'default' : 'outline'}
                    onClick={() =>
                      setFindingSeverity((prev) => (prev === s.value ? null : s.value))
                    }
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={addFinding}
                disabled={!findingName.trim()}
              >
                <Plus className="h-4 w-4" />
                Добавить
              </Button>
            </div>
          </div>

          {/* Фото */}
          <div className="space-y-2">
            <Label>Фото</Label>
            <div className="flex gap-2">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onFilesSelected}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onFilesSelected}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-4 w-4" />
                Камера
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => galleryInputRef.current?.click()}
              >
                <ImageIcon className="h-4 w-4" />
                Из галереи
              </Button>
            </div>

            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-muted"
                  >
                    <img
                      src={p.previewUrl}
                      alt="Фото осмотра"
                      className={cn(
                        'h-full w-full object-cover',
                        p.status === 'uploading' && 'opacity-50',
                      )}
                    />
                    {p.status === 'uploading' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-foreground" />
                      </div>
                    )}
                    {p.status === 'error' && (
                      <button
                        type="button"
                        onClick={() => void uploadOnePhoto(p.file)}
                        title={p.error ?? 'Ошибка загрузки — нажмите, чтобы повторить'}
                        className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-destructive/80 text-destructive-foreground"
                      >
                        <RotateCw className="h-4 w-4" />
                        <span className="text-xs leading-none">повторить</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      aria-label="Убрать фото"
                      className="absolute right-1 top-1 rounded-sm bg-black/55 p-1 text-white"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Заметка */}
          <div className="space-y-2">
            <Label htmlFor="insp-note">Заметка</Label>
            <textarea
              id="insp-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Что видно на поле…"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {showValidation && !draftValid && (
            <p className="text-sm text-destructive">Добавьте находку или заметку</p>
          )}
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
          {photosHaveError && (
            <p className="text-sm text-destructive">
              У части фото не получилось загрузиться — повторите или уберите их.
            </p>
          )}
        </div>

        <div className="mt-6 flex gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Отмена
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={handleSubmit}
            disabled={createMutation.isPending || photosUploading || photosHaveError}
          >
            {createMutation.isPending
              ? 'Сохраняем…'
              : photosUploading
                ? 'Ждём фото…'
                : 'Сохранить осмотр'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default InspectionCreateSheet;
